import { StrictMode, useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { AuthProvider } from '@/auth'
import { createInternalTheme, type Mode } from '@/theme/createInternalTheme'
import { registerInternalEchartsThemes } from '@/lib/echartsTheme'
import { retryDelayWithRetryAfter, retryUnlessClientError } from '@/lib/api'

registerInternalEchartsThemes()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A monitoring wall is a background tab by design. Refetching on every
      // focus would put a burst of cross-org queries on the PRODUCTION writer
      // (capacity_profile="seed" provisions zero read replicas, so cluster-ro
      // resolves to the writer) every time somebody alt-tabs.
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      retry: retryUnlessClientError,
      retryDelay: retryDelayWithRetryAfter,
    },
  },
})

function Root() {
  // Dark by default — this is a wall that stays open. Light is fully supported
  // because investor screenshots go into white decks.
  const [mode, setMode] = useState<Mode>(
    () => (localStorage.getItem('internal.mode') as Mode) ?? 'dark',
  )
  const theme = useMemo(() => createInternalTheme(mode), [mode])
  const toggle = () => {
    const next: Mode = mode === 'dark' ? 'light' : 'dark'
    setMode(next)
    localStorage.setItem('internal.mode', next)
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <App mode={mode} onToggleMode={toggle} />
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
)
